import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { crearPropiedad, editarPropiedad, getPropiedad, subirImagenes } from "../../firebase/propiedades";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TIPOS_OPERACION = ["Venta", "Alquiler"];
const TIPOS_PROPIEDAD = ["Casa", "Terreno", "Hectárea/Campo", "Departamento", "Local/Oficina"];
const ESTADOS = ["Disponible", "Vendida", "Alquilada", "No disponible"]; // ← agregado
const CHAJARI = [-30.7547, -57.9831];

function PinSelector({ posicion, setPosicion }) {
  useMapEvents({
    click(e) {
      setPosicion({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return posicion ? <Marker position={[posicion.lat, posicion.lng]} /> : null;
}

export default function FormPropiedad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esEdicion = Boolean(id);

  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");
  const [archivos, setArchivos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [coordenadas, setCoordenadas] = useState(null);

  const [form, setForm] = useState({
    titulo: "",
    descripcion: "",
    precio: "",
    precioInterno: "",
    operacion: "Venta",
    tipos: [],
    ubicacion: "",
    barrio: "",
    ambientes: "",
    superficie: "",
    estado: "Disponible",
    asfalto: false,
    gasNatural: false,
    imagenes: [],
  });

  const esCampo = form.tipos.includes("Hectárea/Campo");

  useEffect(() => {
    if (esEdicion) {
      getPropiedad(id).then((data) => {
        setForm({
          titulo: data.titulo || "",
          descripcion: data.descripcion || "",
          precio: data.precio || "",
          precioInterno: data.precioInterno || "",
          operacion: data.operacion || "Venta",
          tipos: data.tipos || (data.tipo ? [data.tipo] : []),
          ubicacion: data.ubicacion || "",
          barrio: data.barrio || "",
          ambientes: data.ambientes || "",
          superficie: data.superficie || "",
          estado: data.estado || "Disponible",
          asfalto: data.asfalto || false,
          gasNatural: data.gasNatural || false,
          imagenes: data.imagenes || [],
        });
        const imgs = data.imagenes || (data.imagen ? [data.imagen] : []);
        setPreviews(imgs);
        if (data.coordenadas) setCoordenadas(data.coordenadas);
      });
    }
  }, [id, esEdicion]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  }

  function toggleTipo(tipo) {
    setForm((f) => {
      const yaEsta = f.tipos.includes(tipo);
      return {
        ...f,
        tipos: yaEsta ? f.tipos.filter((t) => t !== tipo) : [...f.tipos, tipo],
      };
    });
  }

  function handleArchivos(e) {
    const nuevos = Array.from(e.target.files);
    setArchivos(nuevos);
    setPreviews(nuevos.map((f) => URL.createObjectURL(f)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.tipos.length === 0) {
      setError("Seleccioná al menos un tipo de propiedad.");
      return;
    }
    setError("");
    setCargando(true);
    try {
      let imagenes = form.imagenes || [];
      if (archivos.length > 0) {
        setSubiendo(true);
        imagenes = await subirImagenes(archivos);
        setSubiendo(false);
      }
      const datos = {
        titulo: form.titulo,
        descripcion: form.descripcion,
        precio: Number(form.precio),
        precioInterno: form.precioInterno ? Number(form.precioInterno) : null,
        operacion: form.operacion,
        tipos: form.tipos,
        tipo: form.tipos[0] || "",
        ubicacion: form.ubicacion,
        barrio: form.barrio,
        ambientes: form.ambientes,
        superficie: form.superficie,
        estado: form.estado,
        asfalto: form.asfalto,
        gasNatural: form.gasNatural,
        imagenes,
        coordenadas: coordenadas || null,
      };
      if (esEdicion) {
        await editarPropiedad(id, datos);
      } else {
        await crearPropiedad(datos);
      }
      navigate("/admin/propiedades");
    } catch (err) {
      setError("Ocurrió un error. Intentá de nuevo.");
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "13px", outline: "none", background: "#fff", boxSizing: "border-box", fontFamily: "system-ui" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: "#6b7280", display: "block", marginBottom: "6px", letterSpacing: "0.5px" };
  const checkboxLabelStyle = { display: "flex", alignItems: "center", gap: "8px", padding: "12px 14px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontSize: "13px", color: "#374151", fontWeight: "600" };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f4f4", fontFamily: "system-ui, sans-serif" }}>

      <div style={{ background: "#fff", borderBottom: "1px solid #e5e5e5", padding: "20px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <button onClick={() => navigate("/admin/propiedades")} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "12px", cursor: "pointer", marginBottom: "4px", display: "block" }}>
            ← Volver a propiedades
          </button>
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: "#111827" }}>
            {esEdicion ? "Editar propiedad" : "Nueva propiedad"}
          </h1>
        </div>
      </div>

      <div style={{ height: "4px", background: "#1a9e2a" }} />

      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "32px 20px" }}>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* INFORMACIÓN BÁSICA */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#111827", marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
              Información básica
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={labelStyle}>TÍTULO</label>
                <input name="titulo" value={form.titulo} onChange={handleChange} required placeholder="Ej: Casa 3 ambientes en centro" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>DESCRIPCIÓN</label>
                <textarea name="descripcion" value={form.descripcion} onChange={handleChange} rows={4} placeholder="Describí la propiedad..." style={{ ...inputStyle, resize: "vertical" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>PRECIO PÚBLICO ($)</label>
                  <input name="precio" type="number" value={form.precio} onChange={handleChange} placeholder="150000" style={inputStyle} />
                  <p style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>Visible para el público</p>
                </div>
                <div>
                  <label style={{ ...labelStyle, color: "#dc2626" }}>🔒 PRECIO INTERNO ($)</label>
                  <input name="precioInterno" type="number" value={form.precioInterno} onChange={handleChange} placeholder="140000" style={{ ...inputStyle, background: "#fef9c3", borderColor: "#fde047" }} />
                  <p style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>Solo visible en el admin</p>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>UBICACIÓN</label>
                  <input name="ubicacion" value={form.ubicacion} onChange={handleChange} placeholder="Ej: Av. San Martín 123" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>BARRIO</label>
                  <input name="barrio" value={form.barrio} onChange={handleChange} placeholder="Ej: Centro" style={inputStyle} />
                </div>
              </div>
            </div>
          </div>

          {/* DETALLES */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#111827", marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
              Detalles
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

              <div>
                <label style={labelStyle}>TIPO DE PROPIEDAD</label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {TIPOS_PROPIEDAD.map((tipo) => {
                    const activo = form.tipos.includes(tipo);
                    return (
                      <button
                        key={tipo}
                        type="button"
                        onClick={() => toggleTipo(tipo)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "20px",
                          border: "1px solid " + (activo ? "#1a9e2a" : "#e5e7eb"),
                          background: activo ? "#1a9e2a" : "#fff",
                          color: activo ? "#fff" : "#374151",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {tipo}
                      </button>
                    );
                  })}
                </div>
                {form.tipos.length === 0 && (
                  <p style={{ fontSize: "11px", color: "#ef4444", marginTop: "6px" }}>
                    Seleccioná al menos uno
                  </p>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>OPERACIÓN</label>
                  <select name="operacion" value={form.operacion} onChange={handleChange} style={inputStyle}>
                    {TIPOS_OPERACION.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>ESTADO</label>
                  <select name="estado" value={form.estado} onChange={handleChange} style={inputStyle}>
                    {ESTADOS.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>DORMITORIOS</label>
                  <input name="ambientes" type="number" value={form.ambientes} onChange={handleChange} placeholder="3" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>
                    {esCampo ? "SUPERFICIE (hectáreas)" : "SUPERFICIE (m²)"}
                  </label>
                  <input
                    name="superficie"
                    type="number"
                    value={form.superficie}
                    onChange={handleChange}
                    placeholder={esCampo ? "50" : "80"}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ paddingTop: "16px", borderTop: "1px solid #f0f0f0" }}>
                <label style={{ ...labelStyle, marginBottom: "10px" }}>SERVICIOS</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <label style={{ ...checkboxLabelStyle, borderColor: form.asfalto ? "#1a9e2a" : "#e5e7eb", background: form.asfalto ? "#f0fdf4" : "#fff" }}>
                    <input type="checkbox" name="asfalto" checked={form.asfalto} onChange={handleChange} style={{ width: "16px", height: "16px", accentColor: "#1a9e2a", cursor: "pointer" }} />
                    🛣 Asfalto
                  </label>
                  <label style={{ ...checkboxLabelStyle, borderColor: form.gasNatural ? "#1a9e2a" : "#e5e7eb", background: form.gasNatural ? "#f0fdf4" : "#fff" }}>
                    <input type="checkbox" name="gasNatural" checked={form.gasNatural} onChange={handleChange} style={{ width: "16px", height: "16px", accentColor: "#1a9e2a", cursor: "pointer" }} />
                    🔥 Gas natural
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* MAPA */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#111827", marginBottom: "8px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
              Ubicación en el mapa
            </h2>
            <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "16px" }}>
              Hacé clic en el mapa donde está ubicada la propiedad para poner el pin.
            </p>
            <div style={{ height: "320px", borderRadius: "12px", overflow: "hidden", border: "1px solid #e5e7eb" }}>
              <MapContainer center={coordenadas ? [coordenadas.lat, coordenadas.lng] : CHAJARI} zoom={14} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <PinSelector posicion={coordenadas} setPosicion={setCoordenadas} />
              </MapContainer>
            </div>
            {coordenadas && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", padding: "10px 14px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                <span style={{ fontSize: "12px", color: "#166534" }}>
                  📍 Lat: {coordenadas.lat.toFixed(5)} · Lng: {coordenadas.lng.toFixed(5)}
                </span>
                <button type="button" onClick={() => setCoordenadas(null)} style={{ background: "none", border: "none", color: "#dc2626", fontSize: "12px", cursor: "pointer", fontWeight: "600" }}>
                  Quitar pin
                </button>
              </div>
            )}
          </div>

          {/* FOTOS */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#111827", marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" }}>
              Fotos
            </h2>
            <input type="file" multiple accept="image/*" onChange={handleArchivos} style={{ fontSize: "13px", color: "#6b7280", width: "100%" }} />
            {previews.length > 0 && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "16px" }}>
                {previews.map((url, i) => (
                  <img key={i} src={url} alt="" style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "8px", border: "2px solid #f0f0f0" }} />
                ))}
              </div>
            )}
          </div>

          {error && <p style={{ color: "#dc2626", fontSize: "13px", textAlign: "center" }}>{error}</p>}

          <button
            type="submit"
            disabled={cargando}
            style={{ background: "#1a9e2a", color: "#fff", border: "none", borderRadius: "12px", padding: "16px", fontWeight: "700", fontSize: "15px", cursor: "pointer", opacity: cargando ? 0.6 : 1 }}
          >
            {subiendo ? "Subiendo fotos..." : cargando ? "Guardando..." : esEdicion ? "Guardar cambios" : "Publicar propiedad"}
          </button>

        </form>
      </div>
    </div>
  );
}
